import { gql, useApolloClient } from '@apollo/client';
import { useCallback, useState } from 'react';
import { useSetAtom } from 'jotai';
import { DaapFilteredCompaniesResult } from '../interfaces/DaapFilteredCompaniesResult';
import { toastAtom } from '../atoms/toastAtom';
import { ToastVariant } from '../constants/toast-variant';
import { companiesAvailableAtom } from '../atoms/companiesAvailableAtom';
import { companiesLoadingAtom } from '../atoms/companiesLoadingAtom';

export const DAAP_FILTERED_COMPANIES_QUERY = gql`
  query DaapFilteredCompanies(
    $contains: String!
    $after: String
    $first: Int!
  ) {
    daapFilteredCompanies(contains: $contains, after: $after, first: $first) {
      edges {
        cursor
        node {
          id
          name
        }
      }
      pageInfo {
        hasNextPage
      }
      totalCount
    }
  }
`;

export const useCompanies = () => {
  const apolloClient = useApolloClient();
  const setToast = useSetAtom(toastAtom);
  const setCompaniesAvailable = useSetAtom(companiesAvailableAtom);
  const setCompaniesLoading = useSetAtom(companiesLoadingAtom);
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>(
    []
  );
  const [hasMore, setHasMore] = useState(true);
  const [scrollLoading, setScrollLoading] = useState(false);
  const [lastCompany, setLastCompany] = useState<string>('');

  const checkMimicEligibility = useCallback(async () => {
    try {
      const result = await apolloClient.query<DaapFilteredCompaniesResult>({
        query: DAAP_FILTERED_COMPANIES_QUERY,
        variables: { contains: '', first: 1, after: '' },
        fetchPolicy: 'network-only',
        context: { skipMimic: true },
      });
      const edges = result.data?.daapFilteredCompanies.edges ?? [];
      setCompaniesAvailable(edges.length > 0);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setCompaniesAvailable(false);
    } finally {
      setCompaniesLoading(false);
    }
  }, [apolloClient, setCompaniesAvailable, setCompaniesLoading]);

  const fetchCompanies = useCallback(
    async (searchTerm: string, first: number, after = '') => {
      try {
        const result = await apolloClient.query<DaapFilteredCompaniesResult>({
          query: DAAP_FILTERED_COMPANIES_QUERY,
          variables: { contains: searchTerm, first, after },
          fetchPolicy: 'network-only',
          context: { skipMimic: true },
        });
        const edges = result.data?.daapFilteredCompanies.edges ?? [];
        setCompanies(edges.map((edge) => edge.node));
        setHasMore(
          result.data?.daapFilteredCompanies.pageInfo.hasNextPage ?? false
        );
        setLastCompany(edges[edges.length - 1]?.cursor ?? '');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        setToast({
          message: 'Failed to load companies. Please try again.',
          variant: ToastVariant.ERROR,
        });
      } finally {
        setScrollLoading(false);
      }
    },
    [apolloClient, setToast]
  );

  const loadMoreCompanies = async (debouncedSearchTerm: string) => {
    setScrollLoading(true);
    try {
      const result = await apolloClient.query<DaapFilteredCompaniesResult>({
        query: DAAP_FILTERED_COMPANIES_QUERY,
        variables: {
          contains: debouncedSearchTerm,
          first: 100,
          after: lastCompany,
        },
        fetchPolicy: 'network-only',
        context: { skipMimic: true },
      });
      const edges = result.data?.daapFilteredCompanies.edges ?? [];
      setCompanies((prev) => [...prev, ...edges.map((edge) => edge.node)]);
      setHasMore(
        result.data?.daapFilteredCompanies.pageInfo.hasNextPage ?? false
      );
      setLastCompany(edges[edges.length - 1]?.cursor ?? '');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setToast({
        message: 'Something went wrong. Please try again.',
        variant: ToastVariant.ERROR,
      });
    } finally {
      setScrollLoading(false);
    }
  };

  return {
    companies,
    hasMore,
    scrollLoading,
    checkMimicEligibility,
    fetchCompanies,
    loadMoreCompanies,
  };
};
